/**
 * @fileoverview Recovers a prism-new socket whose handshake the server refused.
 *
 * prism-new authenticates the Socket.IO handshake with the session bearer. When the bearer has
 * expired the server middleware refuses it, and socket.io-client then stops reconnecting
 * (`socket.active` turns false) until `connect()` is called by hand. This policy refreshes the
 * token and reconnects, a bounded number of times, so an idle tab's chat socket does not stay
 * dead. Transport failures (`socket.active` still true) are left to the client's own backoff.
 */

/** The slice of a socket.io-client `Socket` the policy drives. */
export interface ReauthSocket {
  /** False once the server refused the handshake. */
  readonly active: boolean;
  connect(): unknown;
  on(event: 'connect' | 'connect_error', listener: () => void): unknown;
}

/** Refreshes the token and reconnects after a refused handshake. */
export class PrismNewSocketReauth {
  private attempts = 0;

  /**
   * @param socket The socket to recover.
   * @param refresh Resolves a fresh access token, or null when the session cannot be refreshed.
   * @param maxAttempts Consecutive refusals to retry; a successful connect resets the count.
   */
  constructor(
    private readonly socket: ReauthSocket,
    private readonly refresh: () => Promise<string | null>,
    private readonly maxAttempts: number,
  ) {}

  /** Starts listening to the socket's handshake outcomes. */
  attach(): void {
    this.socket.on('connect', () => {
      this.attempts = 0;
    });
    this.socket.on('connect_error', () => {
      void this.recover();
    });
  }

  private async recover(): Promise<void> {
    if (this.socket.active || this.attempts >= this.maxAttempts) {
      return;
    }
    this.attempts++;
    if (await this.refresh()) {
      this.socket.connect();
    }
  }
}
